// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/restrict-template-expressions */

const aerialApiUri = process.env.aerialPlotConnections.aerialPlotApi.baseUrl;

export function getGroundDataInfo(fieldId, trialId, cropId, clientId, accessToken) {
	return fetch(
		`${aerialApiUri}api/v1/GroundData?fieldId=${fieldId}&trialId=${trialId}&clientId=${clientId}${
			cropId ? `&cropId=${cropId}` : ""
		}`,
		{
			headers: {
				method: "GET",
				Authorization: `Bearer ${accessToken}`,
			},
		},
	)
		.then((res) => {
			return res.json();
		})
		.then((data) => {
			return data;
		});
}

/** Returns the date string, or null when it is empty or moment's "Invalid date". */
function toDateOrNull(date) {
	return date && date !== "Invalid date" ? date : null;
}

/** Builds the planned-timing fields of a ground dataset for its selected timing method. */
function toPlannedTimingJson(groundDataset) {
	const plannedTiming = groundDataset?.plannedTiming;
	switch (groundDataset?.selectedTimingMethodType) {
		case "Date":
			return { growthPhaseDate: toDateOrNull(plannedTiming) };
		case "StartEnd":
			return {
				growthPhaseStartStageId: plannedTiming?.growthStageStart ?? null,
				growthPhaseEndStageId: plannedTiming?.growthStageEnd ?? null,
			};
		case "int":
			return { growthPhaseInteger: plannedTiming ?? null };
		case "Text":
			return { growthPhaseText: plannedTiming ?? null };
		case "Start":
			return { growthPhaseStartStageId: plannedTiming?.growthStageStart ?? null };
		default:
			return {};
	}
}

/** Builds the PlotInfo list of a create or edit request from the outline data. */
function toPlotInfoJson(outlineData) {
	return (outlineData ?? []).map((data) => ({
		plotId: data.plotId,
		range: data.range,
		column: data.column,
		trialId: data.trialId ?? null,
		fieldId: data.fieldId,
	}));
}

/** Builds the GroundDataValidation list of a create or edit request. */
function toGroundDataValidationJson(groundDataValidation) {
	return (groundDataValidation ?? []).map((validation) => ({
		assessmentName: validation.assessmentName,
		dataType: validation.dataType,
		numOfPlots: validation.numOfPlots,
	}));
}

/**
 * Sends a ground data create or edit request. The request model goes as JSON in the "groundData" form field, and
 * the optional CSV goes alongside it as a file, so the body stays multipart.
 */
function sendGroundData(method, groundDataCsv, groundData, accessToken) {
	const groundDataFormData = new FormData();
	// Empty strings become null, the same way the server's form binding treated them before
	groundDataFormData.append(
		"groundData",
		JSON.stringify(groundData, (_key, value) => (value === "" ? null : value)),
	);
	if (groundDataCsv) {
		groundDataFormData.append("groundDataCsv", groundDataCsv, groundDataCsv.name);
	}

	return fetch(`${aerialApiUri}api/v1/GroundData`, {
		method,
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
		body: groundDataFormData,
	})
		.then((res) => res.json())
		.then((data) => data);
}

export function createGroundData(groundDataCsv, newGroundDataSetsVm, outlineData, trialId, accessToken) {
	const groundData = {
		clientId: newGroundDataSetsVm.clientId,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		trialId: trialId ?? null,
		groundDataSets: newGroundDataSetsVm.groundDataSets.map((newGroundDataVm) => ({
			groundDataset: {
				dateCollected: toDateOrNull(newGroundDataVm.groundDataset?.actualDateCollected),
				datasetNumericalId: newGroundDataVm.datasetNumericalId,
				collectedByPersonId: newGroundDataVm.groundDataset?.collectedByPersonId ?? null,
				fieldId: outlineData[0].fieldId,
				trialId: trialId ?? null,
				crop: outlineData[0].crop,
				groundDataTimingMethodId: newGroundDataVm.groundDataset?.selectedTimingMethodId ?? null,
				growthStageId: newGroundDataVm.groundDataset?.growthStageId || null,
				groundDataGrowthPhaseId: newGroundDataVm.groundDataset?.growthPhaseId ?? null,
				trialsToIncludeForField: newGroundDataVm.groundDataset?.trialsToIncludeForField ?? [],
				...toPlannedTimingJson(newGroundDataVm.groundDataset),
			},
			groundDatasetAssessmentData: newGroundDataVm.assessments.map((data, index) => ({
				dateCollected: toDateOrNull(newGroundDataVm.groundDataset?.currentDateCollected),
				name: data.assessment.name,
				groundDataAssessmentId: data.assessment.id,
				averageFor: data?.assessment?.averageFor ?? null,
				groundDataUnitId: data.unit.id,
				groundDataSampleId: data.samplesPerPlot?.id ?? null,
				sampleDefinitionNumber: data.sampleDefinitionNumber?.name ?? "1",
				samplesPerPlot: data.samplesPerPlot?.name ?? "1",
				groundDataDeviceId: data.device?.id ?? null,
				groundDataMethodId: data.method.id,
				groundDataSourceId: data.source.id,
				groundDataSourceName: data.source.name,
				groundDataSampleDefinitionTypeId: data.groundDataSampleDefinitionPart?.groundDataSampleDefinitionTypeId ?? null,
				groundDataSampleDefinitionPartId: data.groundDataSampleDefinitionPart?.id ?? null,
				groundDatasetTemplateId: data.templateId ?? null,
				width: data.width ?? null,
				length: data.length ?? null,
				radius: data.radius ?? null,
				order: index + 1,
				notes: data.notes ?? null,
			})),
		})),
		plotInfo: toPlotInfoJson(outlineData),
		groundDataValidation: toGroundDataValidationJson(newGroundDataSetsVm.groundDataValidation),
	};

	return sendGroundData("POST", groundDataCsv, groundData, accessToken);
}

export function inspectGroundData(groundDataCsv, newGroundDataSetsVm, outlineData, accessToken) {
	const groundDataSetsFormData = new FormData();
	let datasetIndex = 0;
	let index = 0;

	for (const name in newGroundDataSetsVm) {
		let val = newGroundDataSetsVm[name] === "" ? null : newGroundDataSetsVm[name];
		if (typeof val !== "object") {
			groundDataSetsFormData.set(name, val);
		}
	}

	groundDataSetsFormData.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
	groundDataSetsFormData.append("groundDataCsv", groundDataCsv, groundDataCsv.name);

	for (const newGroundDataVm of newGroundDataSetsVm.groundDataSets) {
		groundDataSetsFormData.append(
			`GroundDataSets[${datasetIndex}].groundDataset.DateCollected`,
			newGroundDataVm.groundDataset?.actualDateCollected &&
				newGroundDataVm.groundDataset?.actualDateCollected !== "Invalid date"
				? newGroundDataVm.groundDataset?.actualDateCollected
				: "",
		);
		groundDataSetsFormData.append(
			`GroundDataSets[${datasetIndex}].groundDataset.CollectedByPersonId`,
			newGroundDataVm.groundDataset?.collectedByPersonId ?? "",
		);

		groundDataSetsFormData.append(`GroundDataSets[${datasetIndex}].groundDataset.FieldId`, outlineData[0].fieldId);
		groundDataSetsFormData.append(
			`GroundDataSets[${datasetIndex}].groundDataset.GrowthStageId`,
			newGroundDataVm.groundDataset?.growthStageId ?? "",
		);
		groundDataSetsFormData.append(`GroundDataSets[${datasetIndex}].groundDataset.Crop`, outlineData[0].crop);
		groundDataSetsFormData.append(
			`GroundDataSets[${datasetIndex}].groundDataset.GroundDatasetTemplateId`,
			newGroundDataVm.groundDataset?.groundDatasetTemplateId &&
				newGroundDataVm.groundDataset?.groundDatasetTemplateId !== "none"
				? newGroundDataVm.groundDataset?.groundDatasetTemplateId
				: "",
		);

		groundDataSetsFormData.append(
			`GroundDataSets[${datasetIndex}].groundDataset.GroundDataTimingMethodId`,
			newGroundDataVm.groundDataset?.selectedTimingMethodId ?? "",
		);
		groundDataSetsFormData.append(
			`GroundDataSets[${datasetIndex}].groundDataset.GroundDataGrowthPhaseId`,
			newGroundDataVm.groundDataset?.growthPhaseId ?? "",
		);

		if (newGroundDataVm.groundDataset?.selectedTimingMethodType === "Date") {
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].groundDataset.GrowthPhaseDate`,
				newGroundDataVm.groundDataset?.plannedTiming ?? "",
			);
		} else if (newGroundDataVm.groundDataset?.selectedTimingMethodType === "StartEnd") {
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].groundDataset.GrowthPhaseStartStageId`,
				newGroundDataVm.groundDataset?.plannedTiming?.growthStageStart ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].groundDataset.GrowthPhaseEndStageId`,
				newGroundDataVm.groundDataset?.plannedTiming?.growthStageEnd ?? "",
			);
		} else if (newGroundDataVm.groundDataset?.selectedTimingMethodType === "int") {
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].groundDataset.GrowthPhaseInteger`,
				newGroundDataVm.groundDataset?.plannedTiming ?? "",
			);
		} else if (newGroundDataVm.groundDataset?.selectedTimingMethodType === "Text") {
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].groundDataset.GrowthPhaseText`,
				newGroundDataVm.groundDataset?.plannedTiming ?? "",
			);
		} else if (newGroundDataVm.groundDataset?.selectedTimingMethodType === "Start") {
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].groundDataset.GrowthPhaseStartStageId`,
				newGroundDataVm.groundDataset?.plannedTiming?.growthStageStart ?? "",
			);
		}
		index = 0;
		for (const data of newGroundDataVm.assessments) {
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].DateCollected`,
				newGroundDataVm.groundDataset?.currentDateCollected &&
					newGroundDataVm.groundDataset?.currentDateCollected !== "Invalid date"
					? newGroundDataVm.groundDataset?.currentDateCollected
					: "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].Name`,
				data.assessment.name,
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataAssessmentId`,
				data.assessment.id,
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].AverageFor`,
				data?.assessment?.averageFor ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataUnitId`,
				data.unit.id,
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataSampleId`,
				data.sampleDefinitionNumber?.id ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].SampleDefinitionNumber`,
				data.sampleDefinitionNumber?.name ?? "1",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataDeviceId`,
				data.device?.id ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataMethodId`,
				data.method.id,
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataSourceId`,
				data.source.id,
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataSourceName`,
				data.source.name,
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataSampleDefinitionTypeId`,
				data.groundDataSamplePart?.groundDataSampleDefinitionTypeId ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].GroundDataSampleDefinitionPartId`,
				data.groundDataSamplePart?.id ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].Width`,
				data.width ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].Length`,
				data.length ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].Radius`,
				data.radius ?? "",
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].Order`,
				index + 1,
			);
			groundDataSetsFormData.append(
				`GroundDataSets[${datasetIndex}].GroundDatasetAssessmentData[${index}].Notes`,
				data.notes ?? "",
			);
			index++;
		}

		datasetIndex++;
	}

	index = 0;
	for (const data of outlineData) {
		groundDataSetsFormData.append(`PlotInfo[${index}].PlotId`, data.plotId);
		groundDataSetsFormData.append(`PlotInfo[${index}].Range`, data.range);
		groundDataSetsFormData.append(`PlotInfo[${index}].Column`, data.column);
		groundDataSetsFormData.append(`PlotInfo[${index}].TrialId`, data.trialId ?? "");
		index++;
	}

	index = 0;
	for (const validation of newGroundDataSetsVm.groundDataValidation) {
		groundDataSetsFormData.append(`GroundDataValidation[${index}].AssessmentName`, validation.assessmentName);
		groundDataSetsFormData.append(`GroundDataValidation[${index}].DataType`, validation.dataType);
		groundDataSetsFormData.append(`GroundDataValidation[${index}].NumOfPlots`, validation.numOfPlots);

		index++;
	}
	return fetch(`${aerialApiUri}api/v1/GroundData/InspectGroundData`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
		body: groundDataSetsFormData,
	})
		.then((res) => res.json())
		.then((data) => data);
}

export function editGroundData(groundDataCsv, editGroundDataSetsVm, outlineData, fieldId, trialId, accessToken) {
	const groundData = {
		clientId: editGroundDataSetsVm?.clientId,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		trialId: trialId ?? null,
		fieldId: fieldId ?? null,
		groundDataSets: (editGroundDataSetsVm?.groundDataSets ?? []).map((editGroundDataVm) => ({
			groundDataset: {
				id: editGroundDataVm.datasetId ?? null,
				datasetNumericalId: editGroundDataVm.datasetNumericalId,
				dateCollected: toDateOrNull(editGroundDataVm.groundDataset?.actualDateCollected),
				collectedByPersonId: editGroundDataVm.groundDataset?.collectedByPersonId ?? null,
				fieldId: outlineData[0].fieldId,
				trialId: trialId ?? null,
				crop: outlineData[0].crop,
				groundDataTimingMethodId: editGroundDataVm.groundDataset?.selectedTimingMethodId ?? null,
				growthStageId: editGroundDataVm.groundDataset?.growthStageId || null,
				groundDataGrowthPhaseId: editGroundDataVm.groundDataset?.growthPhaseId ?? null,
				trialsToIncludeForField: editGroundDataVm.groundDataset?.trialsToIncludeForField ?? [],
				...toPlannedTimingJson(editGroundDataVm.groundDataset),
			},
			groundDatasetAssessmentData: editGroundDataVm.assessments.map((data, index) => ({
				id: data.groundDatasetAssessmentDataId ?? null,
				dateCollected: toDateOrNull(editGroundDataVm.groundDataset?.currentDateCollected),
				name: data.assessment.name,
				groundDataAssessmentId: data.assessment.id,
				averageFor: data?.assessment?.averageFor ?? null,
				groundDataUnitId: data.unit.id,
				groundDataSampleId: data.samplesPerPlot?.id ?? null,
				sampleDefinitionNumber: data.sampleDefinitionNumber?.name ?? "1",
				samplesPerPlot: data.samplesPerPlot?.name ?? "1",
				groundDataDeviceId: data.device?.id ?? null,
				groundDataMethodId: data.method.id,
				groundDataSourceId: data.source.id,
				groundDataSourceName: data.source.name,
				groundDataSampleDefinitionTypeId: data.groundDataSampleDefinitionPart?.groundDataSampleDefinitionTypeId ?? null,
				groundDataSampleDefinitionPartId: data.groundDataSampleDefinitionPart?.id ?? null,
				groundDatasetTemplateId: data.templateId ?? null,
				width: data.width ?? null,
				length: data.length ?? null,
				order: index + 1,
				notes: data.notes ?? null,
			})),
		})),
		plotInfo: toPlotInfoJson(outlineData),
		groundDataValidation: toGroundDataValidationJson(editGroundDataSetsVm?.groundDataValidation),
	};

	return sendGroundData("PUT", groundDataCsv, groundData, accessToken);
}

export function deleteDataset(clientId, datasetId, accessToken) {
	return fetch(`${aerialApiUri}api/v1/GroundData/Delete?clientId=${clientId}&datasetId=${datasetId}`, {
		method: "DELETE",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	})
		.then((res) => res.json())
		.then((data) => data);
}

export function eraseDataset(clientId, datasetId, accessToken) {
	return fetch(`${aerialApiUri}api/v1/GroundData/Erase?clientId=${clientId}&datasetId=${datasetId}`, {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	})
		.then((res) => res.json())
		.then((data) => data);
}

export function getGroundDatasets(clientId, accessToken, fieldId = null, trialId = null, protocolId = null) {
	return fetch(
		`${aerialApiUri}api/v1/GroundData/GetGroundDatasets?clientId=${clientId}
		${fieldId ? `&fieldId=${fieldId}` : ""}
		${trialId ? `&trialId=${trialId}` : ""}	
		${protocolId ? `&protocolId=${protocolId}` : ""}`,
		{
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
		},
	)
		.then((res) => res.json())
		.then((data) => data);
}
